import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, ShieldCheck, PenLine } from "lucide-react";

export function ContractDialog({ module, plan, payroll, addonKind, onClose }) {
  const { user } = useAuth();
  const [read, setRead] = useState(false);
  const [signature, setSignature] = useState(user?.name || "");
  const [loading, setLoading] = useState(false);

  const confirm = async () => {
    if (!read) return toast.error("Veuillez lire et accepter le contrat.");
    if (!signature.trim()) return toast.error("Votre signature est requise.");
    setLoading(true);
    try {
      const payload = {
        items: [{ module: module.key, plan: plan.tier }],
        origin_url: window.location.origin,
        signature: signature.trim(),
      };
      if (addonKind === "PAIE") { payload.payroll = true; payload.payroll_plan = plan.tier; }
      if (addonKind === "TAKEOFF") { payload.takeoff = true; payload.takeoff_plan = plan.tier; }
      const { data } = await api.post("/payments/checkout", payload);
      if (data.url) {
        window.location.href = data.url; // Redirection vers le paiement sécurisé Stripe
      } else {
        toast.error("Impossible d'initier le paiement.");
        setLoading(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-white/12 text-white max-w-lg" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <FileText className="w-5 h-5 text-[#EAB308]" /> Contrat d'abonnement
          </DialogTitle>
          <DialogDescription className="text-white/55">
            {plan.name} — {plan.price === "Sur demande" || plan.price === "Inclus" ? plan.price : `${plan.price} $ ${plan.period}`}
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="contract-text"
          className="max-h-56 overflow-y-auto rounded-lg border border-white/10 bg-black/40 p-4 text-xs text-white/60 leading-relaxed space-y-2"
        >
          <p><b className="text-white/80">1. Objet.</b> Le présent contrat concerne l'abonnement au module {module.name} de l'écosystème PROGÈRE, forfait « {plan.name} », fourni par Groupe MJR Construction Inc.</p>
          <p><b className="text-white/80">2. Tarification.</b> {plan.price === "Sur demande" ? "Sur soumission." : `${plan.price} $ CAD par mois, sans engagement.`} Le client peut modifier ou résilier son forfait à tout moment.</p>
          <p><b className="text-white/80">3. Activation.</b> L'accès au module est activé <b className="text-white/80">uniquement après confirmation du paiement</b>. Vous serez redirigé vers le paiement sécurisé Stripe après signature.</p>
          <p><b className="text-white/80">4. Protection des renseignements (Loi 25 - Québec).</b> Les données sont collectées et traitées conformément à la Loi 25. Le client consent à la collecte nécessaire à la prestation du service.</p>
          <p><b className="text-white/80">5. Facturation.</b> La facturation est mensuelle et récurrente. Les taxes applicables (TPS/TVQ) s'ajoutent.</p>
          <p><b className="text-white/80">6. Confidentialité & sécurité.</b> PROGÈRE applique des mesures de sécurité et de journalisation. Voir la politique de confidentialité sur progere.ca.</p>
          <p><b className="text-white/80">7. Responsabilité du Client.</b> Le Client demeure seul responsable de la gestion de son entreprise et des décisions prises à partir des données de PROGÈRE. PROGÈRE est un outil d'aide à la gestion et ne remplace pas un comptable, fiscaliste, avocat, professionnel de la paie, ingénieur ou estimateur. Le Client doit vérifier et approuver les renseignements (heures, taxes, calculs, soumissions, contenus IA) avant toute utilisation, transmission ou décision.</p>
          <p><b className="text-white/80">8. Limitation de responsabilité.</b> Dans toute la mesure permise par les lois applicables, la responsabilité totale de Groupe MJR Construction inc. se limite aux frais d'abonnement payés pour le module concerné au cours des 12 mois précédents; les dommages indirects (pertes de profits, de contrat, retards) sont exclus. Cette limite ne s'applique pas à une faute lourde ou intentionnelle, ni lorsque la loi l'interdit.</p>
          <p><b className="text-white/80">9. Signature électronique.</b> La signature électronique ci-dessous vaut acceptation ferme et engagement contractuel. Conditions complètes&nbsp;: <a href="/conditions" target="_blank" rel="noopener" className="text-[#EAB308] underline">progere.ca/conditions</a>.</p>
        </div>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="contract-accept">
          <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} className="mt-1 h-4 w-4 accent-[#EAB308]" />
          <span className="text-sm text-white/70 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-[#EAB308]" /> J'ai lu et j'accepte l'ensemble du contrat, les <a href="/conditions" target="_blank" rel="noopener" className="text-[#EAB308] underline">conditions d'utilisation</a> (dont les clauses de responsabilité et de limitation) et la Loi 25.
          </span>
        </label>

        <div className="space-y-1.5">
          <label className="text-xs text-white/50 flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" /> Signature électronique</label>
          <Input
            data-testid="contract-signature"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Tapez votre nom complet"
            className="bg-black/40 border-white/15 text-white h-11 font-[cursive] italic placeholder:text-white/30"
          />
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/15 bg-transparent text-white hover:bg-white/5">
            Annuler
          </Button>
          <Button
            data-testid="contract-confirm"
            onClick={confirm}
            disabled={loading || !read || !signature.trim()}
            className="flex-1 bg-[#EAB308] text-black font-semibold hover:opacity-90 transition-opacity"
          >
            {loading ? "Redirection vers le paiement..." : "Signer et payer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
